import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";
import { BadgeCheck, Download, Share2, Home, Loader2 } from "lucide-react";
import { waLink } from "@/lib/constants";

export default function VisitorPass({ pass }) {
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const verifyUrl = `${window.location.origin}/pass/${pass.visitor_id}`;

  const download = async () => {
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: null, scale: 2 });
      const link = document.createElement("a");
      link.download = `OneirosPass-${pass.visitor_id}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } finally {
      setDownloading(false);
    }
  };

  const share = () => {
    const text = `My Oneiros Varachha visitor pass ${pass.visitor_id}. ${verifyUrl}`;
    window.open(waLink(pass.mobile, text), "_blank");
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8"
      style={{ background: "linear-gradient(180deg,#081226,#0D1C38 55%,#081226)" }}>
      <div className="w-full max-w-sm fade-up">
        <div className="flex items-center justify-center gap-2 mb-5 text-emerald-400 font-display font-bold tracking-widest text-sm" data-testid="entry-confirmed-banner">
          <BadgeCheck size={20} /> ENTRY CONFIRMED
        </div>

        <div ref={cardRef} className="rounded-3xl overflow-hidden border-2 border-[#E8752A] shadow-2xl pass-pattern"
          style={{ background: "linear-gradient(160deg,#0f2042,#081226)" }} data-testid="visitor-pass-card">
          <div className="px-6 pt-6 pb-4 flex items-center justify-between border-b border-[#E8752A]/20">
            <div>
              <div className="font-display font-extrabold text-white text-lg tracking-tight">ONEIROS <span className="text-[#E8752A]">VARACHHA</span></div>
              <div className="text-[9px] tracking-[0.28em] text-slate-400 mt-1">OFFICIAL VISITOR PASS</div>
            </div>
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg,#FFA14D,#E85D1F)" }}>
              <BadgeCheck size={18} className="text-white" />
            </div>
          </div>

          <div className="px-6 py-5 text-center">
            <p className="text-[10px] tracking-[0.24em] text-[#E8752A] uppercase mb-1">Welcome</p>
            <h2 className="font-display text-2xl font-extrabold text-white" data-testid="pass-visitor-name">{pass.full_name}</h2>
            <p className="font-mono-num text-[#FFD9B8] tracking-widest text-lg mt-1" data-testid="pass-visitor-id">{pass.visitor_id}</p>
            {pass.is_returning && (
              <span className="inline-block mt-2 text-[10px] font-bold px-3 py-1 rounded-full bg-[#E8752A]/15 text-[#FFD9B8]">
                RETURNING VISITOR · Visit #{pass.total_visits}
              </span>
            )}
          </div>

          <div className="mx-6 bg-white rounded-2xl p-4 flex justify-center">
            <QRCodeCanvas value={verifyUrl} size={150} fgColor="#081226" level="M" />
          </div>

          <div className="px-6 py-5 grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-[10px] tracking-wider text-slate-400 uppercase">Date</p><p className="text-white font-semibold">{pass.date}</p></div>
            <div><p className="text-[10px] tracking-wider text-slate-400 uppercase">Time of Entry</p><p className="text-white font-semibold">{pass.time}</p></div>
            <div className="col-span-2"><p className="text-[10px] tracking-wider text-slate-400 uppercase">Purpose of Visit</p><p className="text-white font-semibold">{pass.visiting_for}</p></div>
            {pass.interests?.length > 0 && (
              <div className="col-span-2">
                <p className="text-[10px] tracking-wider text-slate-400 uppercase mb-1.5">Selected Interests</p>
                <div className="flex flex-wrap gap-1.5">
                  {pass.interests.map((i) => (
                    <span key={i} className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[#E8752A]/15 text-[#FFD9B8]">{i}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="bg-emerald-500/10 border-t border-emerald-500/20 py-3 text-center">
            <p className="text-emerald-400 font-display font-bold text-sm tracking-wide">Welcome to Oneiros Varachha</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-5">
          <button data-testid="pass-download-btn" onClick={download} disabled={downloading}
            className="h-12 rounded-2xl font-bold text-white flex items-center justify-center gap-2" style={{ background: "linear-gradient(135deg,#FFA14D,#E85D1F)" }}>
            {downloading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />} Save Pass
          </button>
          <button data-testid="pass-share-btn" onClick={share} className="h-12 rounded-2xl font-bold text-white flex items-center justify-center gap-2 bg-[#25D366]">
            <Share2 size={18} /> Share
          </button>
        </div>
        <button data-testid="pass-home-btn" onClick={() => navigate("/")} className="w-full mt-3 h-11 rounded-2xl font-semibold text-slate-300 flex items-center justify-center gap-2 border border-slate-700">
          <Home size={16} /> Done
        </button>
      </div>
    </div>
  );
}
